import React, { useState, useRef } from "react";
import { Button } from "@packages/ui";
import { CircularProgress, Dialog, Box, Typography, DialogActions } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import useDivePolling from "src/hooks/useDivePolling";
import { athenaURL } from "../../env";
import getBrandFromUrl from "src/utils/getBrandFromUrl";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    minHeight: 250,
    textAlign: "center",
  },
  spinner: {
    marginBottom: theme.spacing(2),
  },
  successIcon: {
    fontSize: 60,
    color: "green",
    marginBottom: theme.spacing(2),
  },
  message: {
    marginTop: theme.spacing(2),
    fontSize: 16,
    fontWeight: 500,
  },
  dialogActions: {
    padding: theme.spacing(2),
    justifyContent: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  closeButton: {
    minWidth: 120,
  },
}));

const DiveButton = ({
  firstName,
  lastName,
  phone,
  email,
  metadata = {},
  caseData = [],
  sendSms = false,
  sendEmail = false,
  disabled = false,
  title = "Verify Identity",
  callbackUrl = "",
  virId = "",
  endpointName = "otherDriver",
  onVerificationComplete = null,
  onClickValidate = null,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const timeoutRef = useRef(null);
  const brand = getBrandFromUrl();
  const MAX_WAIT_TIME = 600; // 10 minutes in seconds

  // Get authorization and base URL from Redux
  const authorization = useSelector((state) => state.vir.athenaAuth.authorization);

  const handleVerificationSuccess = (result) => {
    console.log("DIVE verification successful:", result);
    setVerificationComplete(true);

    // Call callback immediately when verification is received to disable button right away
    if (onVerificationComplete) {
      onVerificationComplete(true);
    }

    // Auto-close dialog after 2 seconds to show success message
    timeoutRef.current = setTimeout(() => {
      setDialogOpen(false);
      setVerificationComplete(false);
      setLoading(false);
    }, 2000);
  };

  const handleVerificationError = (error) => {
    console.error("DIVE verification error:", error);
    setVerificationComplete(false);
    setDialogOpen(false);
    setLoading(false);
    alert(`Verification failed: ${error?.message || "Unknown error"}`);
  };

  const { disconnect, formattedTime, maxWaitTimeFormatted, timedOut } = useDivePolling(
    dialogOpen ? virId : null,
    authorization,
    athenaURL,
    endpointName,
    handleVerificationSuccess,
    handleVerificationError
  );

  const handleVerify = async () => {
    // Validate form fields before opening dialog
    if (onClickValidate) {
      const isValid = await onClickValidate();
      if (!isValid) {
        return;
      }
    }

    setLoading(true);
    setDialogOpen(true);

    try {
      // Build callback URL if not provided
      let finalCallbackUrl = callbackUrl;
      if (!finalCallbackUrl) {
        const protocol = window.location.protocol;
        const host = window.location.hostname;
        const port = window.location.port ? `:${window.location.port}` : "";
        finalCallbackUrl = `${protocol}//${host}${port}/vehicleincidents/dive-finish?vir=${virId}&category=${endpointName}&brand=${brand}`;
      }

      console.log("[DiveButton] Using callback URL:", finalCallbackUrl);

      const requestBody = {
        firstName,
        lastName,
        phone,
        email,
        metadata,
        sendSms,
        sendEmail,
        callbackUrl: finalCallbackUrl,
        case: caseData.length > 0 ? caseData : [{ name: 1, value: "test case" }],
      };

      const response = await fetch("https://api-dvsonline.idscan.net/api/v2/private/Applicants", {
        method: "POST",
        headers: {
          Authorization: "Bearer sk_584b39a0-fb65-4fd9-943c-9aca358e17b0",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.shortValidationLink) {
        throw new Error("No validation link received from API");
      }

      // Open validation link in new tab
      setTimeout(() => {
        window.open(data.shortValidationLink, "_blank");
      });
    } catch (err) {
      console.error("Verification error:", err);
      setDialogOpen(false);
      setLoading(false);
      alert(`Verification error: ${err.message}`);
    }
  };

  const handleDialogClose = () => {
    // Only allow closing if verification is complete, never allow backdrop clicks during loading
    if (verificationComplete) {
      disconnect();
      setDialogOpen(false);
      setVerificationComplete(false);
      setLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleCancelVerification = () => {
    // Allow user to cancel during loading
    disconnect();
    setDialogOpen(false);
    setVerificationComplete(false);
    setLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Close dialog automatically when timeout occurs
  React.useEffect(() => {
    if (timedOut) {
      handleCancelVerification();
    }
  }, [timedOut]);

  return (
    <>
      <Button
        variant="contained"
        component="label"
        fullWidth
        disabled={disabled || loading}
        onClick={handleVerify}
      >
        {loading ? <CircularProgress size={24} /> : title}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        disableEscapeKeyDown={true}
        onBackdropClick={verificationComplete ? handleDialogClose : undefined}
        maxWidth="sm"
        fullWidth
      >
        <Box className={classes.dialogContent}>
          {verificationComplete ? (
            <>
              <CheckCircleOutlineIcon className={classes.successIcon} />
              <Typography className={classes.message}>Verification Successful!</Typography>
            </>
          ) : (
            <>
              <CircularProgress size={60} className={classes.spinner} />
              <Typography className={classes.message}>
                Verifying driver&apos;s license...
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 12 }}>
                {formattedTime}
              </Typography>
            </>
          )}
        </Box>
        <DialogActions className={classes.dialogActions}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancelVerification}
            className={classes.closeButton}
            startIcon={<CloseIcon />}
          >
            {verificationComplete ? "Close" : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DiveButton;
